import Graphic from "@arcgis/core/Graphic";
import { GISType } from "./gis-types";
import Geometry from "@arcgis/core/geometry/Geometry";
import Polygon from "@arcgis/core/geometry/Polygon";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";

export function encodeFormData(data: [string, any][]) {
  return data
    .map(([key, value]) => {
      if (typeof value !== "string" && typeof value !== "number")
        value = JSON.stringify(value);
      value = encodeURIComponent(value);
      return `${encodeURIComponent(key)}=${value}`;
    })
    .join("&");
}

export function createBodyFromGraphics(
  graphic: Graphic[],
  gisType: GISType,
  token: string,
  Id: number,
  ActivityNum: number,
): [string, string | number | Partial<Graphic>[]][] {
  const body: [string, string | number | Partial<Graphic>[]][] = [
    ["f", "pjson"],
    ["token", token],
  ];
  graphic.forEach((g) => {
    g.attributes ??= [];
    const attributes = g.attributes;
    attributes[gisType.Id] = Id;
    if (ActivityNum) attributes["ActivityNum"] = ActivityNum;
  });
  body.push(["features", graphic]);
  return body;
}

export function updateBodyFromGraphic(
  graphic: Graphic,
  gisType: GISType,
  token: string,
  Id: number,
): [string, string | Partial<Graphic>[]][] {
  const updateFeature: Partial<Graphic>[] = [
    {
      geometry: graphic.geometry,
      attributes: {
        ...graphic.attributes,
      },
    },
  ];
  updateFeature[0].attributes[gisType.Id] = Id; // a newly drawn shape won't have this property set
  switch (gisType) {
    case GISType.Covenant:
      break; // Covs don't have proposed/accepted shapes
    case GISType.StateRegister:
      updateFeature[0].attributes["bGISAccepted"] = 1; // SR shapes are always accepted.
      break;
    default:
      // Other resource types (HCPIs and Sites)
      updateFeature[0].attributes["bGISAccepted"] = 0; // false, Proposed.
  }

  return [
    ["updates", updateFeature],
    ["f", "pjson"],
    ["token", token],
  ];
}

export async function deleteShapes(
  objectIdsToDelete: number[],
  deleteURL: string,
  token: string,
  failureMessage: string,
) {
  const endpoint = new URL(deleteURL);
  const params = new URLSearchParams({
    f: "pjson",
    token: token,
  });
  params.append("objectIds", objectIdsToDelete.join(","));
  endpoint.search = params.toString();
  await fetch(endpoint.toString(), {
    method: "POST",
  }).catch((err) => {
    console.error(failureMessage, err);
    throw new Error(failureMessage);
  });
}

export const validateGraphicPolygon: (graphic: Graphic) => boolean = (
  graphic: Graphic,
) => isPolygon(graphic.geometry) && validatePolygon(graphic.geometry);
export const validatePolygon: (polygon: Polygon) => boolean = (
  polygon: Polygon,
) => filterEmptyPolygon(polygon) && filterInvalidPolygon(polygon);
const filterEmptyPolygon: (polygon: Polygon) => boolean = (polygon: Polygon) =>
  polygon.rings.length != 0;
const filterInvalidPolygon = (polygon: Polygon) => {
  if (polygon.spatialReference.equals(SpatialReference.WGS84)) {
    // Ensure coordinates fall within min/max lat/long
    for (const ring of polygon.rings) {
      for (const point of ring) {
        if (Math.abs(point[0]) > 180 || Math.abs(point[1]) > 90) return false;
      }
    }
  }
  return true;
};

export const isGraphicHit = (hit: __esri.ViewHit): hit is __esri.GraphicHit =>
  hit.type === "graphic";

export const isPolygon = (geometry: Geometry): geometry is Polygon =>
  geometry.type === "polygon";

export const setShapeOnBeforeUnloadEventHandler = async (
  parent: DotNet.DotNetObject | null,
) => {
  await unsetShapeOnBeforeUnloadEventHandler(parent);
  if (parent != null) {
    await parent.invokeMethodAsync("SetShapeOnBeforeUnloadEventHandler");
  } else {
    return;
  }
};
export const unsetShapeOnBeforeUnloadEventHandler = async (
  parent: DotNet.DotNetObject | null,
) => {
  if (parent != null) {
    await parent.invokeMethodAsync("UnsetShapeOnBeforeUnloadEventHandler");
  } else {
    return;
  }
};
